package org.example.project.service

import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.utils.All

object SnapApi {
    private const val EMPTY_FIELD = "~"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadPendingRequests(userId: String, firmenId: String = ""): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call("snap/pending/${apiPart(userId)}/${apiPart(firmenId)}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadPartners(userId: String, firmenId: String = ""): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call("snap/partners/${apiPart(userId)}/${apiPart(firmenId)}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun createRegistrationInvitation(userId: String, firmenId: String): SnapResponse? {
        val raw = LumaticaApiManager.call("snap/registration-invitation-create/${apiPart(userId)}/${apiPart(firmenId)}")
        return decodeResponse(raw, markCompany = false)
    }

    suspend fun searchPartners(filters: Map<String, String>): List<All> {
        val raw = loadWithTabularFilters("snap/partners-tabular", filters)
        return runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadHofCompanies(userId: String, firmenId: String = ""): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call("snap/hof-companies/${apiPart(userId)}/${apiPart(firmenId)}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun loadAuftragCompanies(userId: String, firmenId: String = ""): List<All> {
        if (userId.isBlank()) return emptyList()
        val raw = runCatching {
            LumaticaApiManager.call("snap/auftrag-companies/${apiPart(userId)}/${apiPart(firmenId)}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun previewBySnapKey(
        userId: String,
        snapKey: String,
        firmenId: String = ""
    ): All? {
        if (userId.isBlank() || snapKey.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf("snap", "preview-key", userId, snapKey, firmenId).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
    }

    suspend fun connectBySnapKey(
        userId: String,
        snapKey: String,
        firmenId: String = ""
    ): SnapResponse? {
        if (userId.isBlank() || snapKey.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf(
                    "snap",
                    "connect-key",
                    userId,
                    snapKey,
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return decodeResponse(raw, markCompany = true)
    }

    suspend fun sendBySnapKey(
        userId: String,
        snapKey: String,
        firmenId: String = ""
    ): SnapResponse? {
        if (userId.isBlank() || snapKey.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf("snap", "send-key", userId, snapKey, firmenId).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return decodeResponse(raw, markCompany = false)
    }

    suspend fun savePartnerSettings(
        userId: String,
        partnerFirmenId: String,
        snapKontaktlisteTeilen: Boolean,
        snapZeitfensterBuchen: Boolean,
        snapHofFreigabe: Boolean,
        snapAuftraegeTeilen: Boolean,
        firmenId: String = ""
    ): SnapResponse? {
        if (userId.isBlank() || partnerFirmenId.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf(
                    "snap",
                    "partner-save",
                    userId,
                    partnerFirmenId,
                    snapKontaktlisteTeilen.toString(),
                    snapZeitfensterBuchen.toString(),
                    snapHofFreigabe.toString(),
                    snapAuftraegeTeilen.toString(),
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return decodeResponse(raw, markCompany = false)
    }

    suspend fun requestPartner(
        userId: String,
        partnerFirmenId: String,
        firmenId: String = ""
    ): SnapResponse? {
        if (userId.isBlank() || partnerFirmenId.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf(
                    "snap",
                    "request-partner",
                    userId,
                    partnerFirmenId,
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return decodeResponse(raw, markCompany = false)
    }

    suspend fun deletePartner(
        userId: String,
        partnerFirmenId: String,
        firmenId: String = ""
    ): SnapResponse? {
        if (userId.isBlank() || partnerFirmenId.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf(
                    "snap",
                    "partner-delete",
                    userId,
                    partnerFirmenId,
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return decodeResponse(raw, markCompany = true)
    }

    suspend fun respond(
        requestId: String,
        userId: String,
        firmenId: String = "",
        accepted: Boolean
    ): SnapResponse? {
        if (requestId.isBlank() || userId.isBlank()) return null
        val raw = runCatching {
            LumaticaApiManager.call(
                listOf(
                    "snap",
                    "respond",
                    requestId,
                    userId,
                    accepted.toString(),
                    firmenId
                ).joinToString("/") { apiPart(it) }
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return null
        return decodeResponse(raw, markCompany = true)
    }

    private fun decodeResponse(raw: String, markCompany: Boolean): SnapResponse? {
        val response = runCatching { json.decodeFromString<SnapResponse>(raw) }.getOrNull()
        if (response?.ok == true) {
            markSnapChanged(markCompany)
        }
        return response
    }

    private fun markSnapChanged(markCompany: Boolean) {
        GlobalManager.markJsonChanged("snap", "service/snap/requests/request.json")
        GlobalManager.markJsonChanged("settings.json", "register/snapsettings/settings.json")
        if (markCompany) {
            GlobalManager.markJsonChanged("mandant.json", "register/mandanten/mandant.json")
        }
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) EMPTY_FIELD else value) as String
}

typealias SnapResponse = All
